// src/components/contracts/ItemListEditor.jsx
// The repeatable row editor behind Appendix 3 — Included Hardware.
//
// Structurally the Foundational Upgrade Plan's line-item editor with the
// pricing removed: this is pure scope/inventory, not priced work. The two
// conventions worth carrying over are the ones that were learned the hard way
// there:
//   • quantity is type="text" inputMode="decimal", NOT type="number". A numeric
//     input seeded with a literal 0 appends rather than replaces, which is what
//     produced the "010" display bug in Foundational.
//   • add and edit go through ONE row component, so the same item cannot be
//     described two different ways depending on how it was reached.
//
// `hasQuantity` false is Software: an application has no unit count, and the
// renderer omits the "N x " prefix for that category, so showing a Qty box
// would invite a value that is then silently dropped.
import { useState } from "react";

const row = { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 };
const inp = {
  padding: "6px 8px", borderRadius: 6, fontSize: 12, fontFamily: "inherit",
  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)", color: "var(--text,#222)",
};
const btn = (primary) => ({
  padding: "4px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});

export default function ItemListEditor({ label, items, hasQuantity = true, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const [qty, setQty] = useState("1");
  const [desc, setDesc] = useState("");

  const commit = (next) => onChange(next);

  const add = () => {
    const d = desc.trim();
    if (!d) return;                       // a row with no description renders nothing
    commit([...list, hasQuantity ? { qty: qty.trim() || "1", description: d } : { description: d }]);
    setQty("1"); setDesc("");
  };

  const patch = (i, p) => commit(list.map((it, k) => (k === i ? { ...it, ...p } : it)));
  const remove = (i) => commit(list.filter((_, k) => k !== i));

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text3,#888)" }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: "var(--text3,#aaa)" }}>
          {list.length ? `${list.length} item${list.length === 1 ? "" : "s"}` : "none — this category is left out of the contract"}
        </span>
      </div>

      {list.map((it, i) => (
        <div key={i} style={row}>
          {hasQuantity && (
            <input style={{ ...inp, width: 54 }} type="text" inputMode="decimal" value={it.qty ?? ""}
                   onChange={(e) => patch(i, { qty: e.target.value })} aria-label="Quantity" />
          )}
          {hasQuantity && <span style={{ fontSize: 11, color: "var(--text3,#999)" }}>x</span>}
          <input style={{ ...inp, flex: 1 }} value={it.description ?? ""}
                 onChange={(e) => patch(i, { description: e.target.value })}
                 placeholder="Description" aria-label="Description" />
          <button style={btn(false)} onClick={() => remove(i)} title="Remove this item">✕</button>
        </div>
      ))}

      <div style={row}>
        {hasQuantity && (
          <input style={{ ...inp, width: 54 }} type="text" inputMode="decimal" value={qty}
                 onChange={(e) => setQty(e.target.value)} aria-label="Quantity" />
        )}
        {hasQuantity && <span style={{ fontSize: 11, color: "var(--text3,#999)" }}>x</span>}
        <input style={{ ...inp, flex: 1 }} value={desc}
               onChange={(e) => setDesc(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               placeholder={hasQuantity ? "e.g. HP Pro Mini 260 G9 Desktop PC" : "e.g. Office 365 Suite"} />
        <button style={btn(true)} onClick={add}>+ Add item</button>
      </div>
    </div>
  );
}
